/**
 * useNPS Hook
 * React hook for managing NPS surveys with eligibility, submission, and skip.
 *
 * Frontend guards (module-level singletons, shared across all hook instances):
 *
 *   1. One per calendar day  — once any NPS is shown today, all subsequent
 *      checkEligibility calls return ineligible until midnight.
 *      Stored in localStorage so it survives page navigation.
 *
 *   2. One at a time         — while an NPS modal is open, no other trigger
 *      can open a second one on top. Released when the modal closes via
 *      submit, skip, or releaseNPSLock().
 */

import { useState, useCallback } from 'react'
import npsService from '../services/nps.service'

// ─── Module-level singletons (shared across all useNPS() instances) ────────────

const NPS_DATE_KEY = 'nps_last_shown_date'

/** Returns true if any NPS modal has already been shown today */
const isShownToday = () => {
  try {
    return localStorage.getItem(NPS_DATE_KEY) === new Date().toDateString()
  } catch {
    return false
  }
}

/** Mark that an NPS was shown today and lock out further triggers */
const flagNPSShown = () => {
  _npsActive = true
  try {
    localStorage.setItem(NPS_DATE_KEY, new Date().toDateString())
  } catch { /* localStorage unavailable */ }
}

/** Release the "currently showing" lock (called on close/submit/skip) */
const releaseNPSLock = () => {
  _npsActive = false
}

// Tracks whether any NPS modal is currently open (in-memory, resets on page load)
let _npsActive = false

// ──────────────────────────────────────────────────────────────────────────────

export const useNPS = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Check if user is eligible for NPS survey.
   * Applies frontend guards before hitting the backend:
   *   - Already shown an NPS today → ineligible
   *   - Another NPS modal is currently open → ineligible
   */
  const checkEligibility = useCallback(async (triggerType) => {
    // Frontend guard: one per day across all trigger types
    if (isShownToday()) {
      return { eligible: false, reason: 'SHOWN_TODAY' }
    }
    // Frontend guard: one at a time — no stacking
    if (_npsActive) {
      return { eligible: false, reason: 'NPS_ALREADY_ACTIVE' }
    }

    setLoading(true)
    setError(null)
    try {
      const result = await npsService.checkEligibility(triggerType)
      // If backend also says eligible, set both guards immediately
      if (result?.eligible) {
        flagNPSShown()
      }
      return result
    } catch (err) {
      const errorMsg = err.message || 'Failed to check NPS eligibility'
      setError(errorMsg)
      console.error('useNPS checkEligibility error:', err)
      return { eligible: false, reason: 'ERROR' }
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Submit NPS response — releases the active lock when done.
   */
  const submitResponse = useCallback(async ({ triggerType, rating, comment, selectedReason, npsData }) => {
    setLoading(true)
    setError(null)
    try {
      await npsService.submitResponse({ triggerType, rating, comment, selectedReason, npsData })
      return true
    } catch (err) {
      const errorMsg = err.message || 'Failed to submit NPS response'
      setError(errorMsg)
      console.error('useNPS submitResponse error:', err)
      return false
    } finally {
      releaseNPSLock()
      setLoading(false)
    }
  }, [])

  /**
   * Skip NPS survey — releases the active lock when done.
   */
  const skip = useCallback(async (triggerType) => {
    setLoading(true)
    setError(null)
    try {
      await npsService.skip(triggerType)
      return true
    } catch (err) {
      const errorMsg = err.message || 'Failed to skip NPS survey'
      setError(errorMsg)
      console.error('useNPS skip error:', err)
      return false
    } finally {
      releaseNPSLock()
      setLoading(false)
    }
  }, [])

  return {
    checkEligibility,
    submitResponse,
    skip,
    releaseNPSLock,
    loading,
    error,
  }
}
